import { GQL_ID, PageInfo, Product, QueryVariablesTypes } from '@@types'
import { gql, useQuery } from '@apollo/client'
import { useCallback, useMemo } from 'react'

import { ProductWithOfferFragment } from '@entities/Product/fragments'

import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'


type AllProductsQuery = {
  allProducts: {
    edges: Product[]
    pageInfo: PageInfo
  }
}

type AllProductsQueryVariables = QueryVariablesTypes & {
  style: string
  shop_id: GQL_ID
}

export const GQL_QUERY_ALL_PRODUCTS = gql`
  query allProducts (
    $filter: NestedFilterInput,
    $pager: CommonPagerInput,
    $sort: CommonSortInput
    $style: String
    $shop_id: ID!
  ) {
    allProducts(
      filter: $filter, 
      pager: $pager, 
      sort: $sort,
      shop_id: $shop_id
    ) {
      edges {
        ...ProductWithOffer
      }
      pageInfo {
        page
        perPage
        lastPage
        next
      }
    }
  }
  ${ProductWithOfferFragment}
`

export const useFetchAllProducts = (variables: AllProductsQueryVariables, skip?: boolean) => {
  const { data, loading, error, refetch, fetchMore, ...rest } = useQuery<AllProductsQuery, AllProductsQueryVariables>(
    GQL_QUERY_ALL_PRODUCTS,
    {
      variables,
      fetchPolicy: 'cache-and-network',
      skip: !!skip
    }
  )

  const products = useMemo(() => data?.allProducts?.edges ?? [], [ data?.allProducts.edges ])
  const pageInfo = useMemo(() => data?.allProducts?.pageInfo, [ data?.allProducts.pageInfo ])

  const { hasMore, fetchNextPage, clearPagination } = useFetchMorePagination<AllProductsQuery, AllProductsQueryVariables>({
    loading,
    usePager: true,
    pageVariable: 'pager',
    fetchMore,
    updaterFunction (cached: AllProductsQuery, fetched: AllProductsQuery) {
      const prev = cached?.allProducts?.edges ?? []
      const curr = fetched?.allProducts?.edges ?? []

      return {
        hasMore: curr.length > 0,
        result: {
          ...cached,
          allProducts: {
            ...cached.allProducts,
            edges: [ ...prev, ...curr ]
          }
        }
      }
    }
  })

  const refresh = useCallback(() => {
    if (refetch) {
      refetch()
      clearPagination()
    }
  }, [ clearPagination, refetch ])

  return {
    products,
    pageInfo,
    data,
    loading,
    error,
    refresh,
    hasMore,
    fetchNextPage,
    ...rest
  }
}